import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Typography, Box, Checkbox, useMediaQuery, useTheme } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { MTable, RepealableName } from '../../../../../Components';
import { HtmlView } from '../../../../../Components/Html/Html.View';
import { OwnNotes } from '../../../../../Components/OwnNotes';
import { useFilter } from '../../../../../Components/ListFilter';
import { AptorApi, IGridQueryRequest, INamedEntity, OrderDirection } from '../../../../../Api/AptorApi';
import { useAptorApi } from '../../../../../Api';
import { MTableActions, MTableToolbar, Query } from '@material-table/core';
import { makeStyles } from '@material-ui/core/styles';
import { StepButtonBar } from '../OtherRequirementWizard/StepButtonBar';
import { StepProps } from './types';
import UpdateConnectionInfo from './UpdateConnectionInfo';
import moment from 'moment';
import { RequirementsListFilter } from './CustomerRequirements.List.Filter';

const CenteredActions = (props: any) => (
  <Box style={{ width: 100, display: 'flex', justifyContent: 'center' }}><MTableActions {...props} /></Box>
);

const useConnectionToolbarStyles = makeStyles({ toolbar: { paddingLeft: '0 !important' } });
const useConnectionTableStyles = makeStyles({
  root: { '& tbody > tr > td:first-child': { paddingLeft: '0 !important', paddingRight: '0 !important' } },
});

const ConnectionToolbar = (props: any) => {
  const classes = useConnectionToolbarStyles();
  return <MTableToolbar {...props} className={`${props.className || ''} ${classes.toolbar}`} />;
};

interface Step4RequirementTableProps extends StepProps {
  selectedCompanyUnit?: { id: number; name: string };
  connectionAddedToReqs: Array<{ id: number; name: string; number: string }>;
  connectionRemovedFromReqs: Array<{ id: number; name: string; number: string }>;
  requirementHasConnection: (lawId: number) => boolean;
  addRequirementConnection: (law: { id: number; name: string; number: string }) => void;
  removeRequirementConnection: (law: { id: number; name: string; number: string }) => void;
  currentStepName: string;
  updateConnectionInfo: any;
  setUpdateConnectionInfo: (info: any) => void;
}

export interface IRequirement extends INamedEntity {
  description: string;
  area: string;
  changedDate?: Date;
  effectiveTo?: Date;
  companyUnits: Array<INamedEntity & { note: string | null }>;
  hasReports: boolean;
  connections: {
    units: number;
    processes: number;
    aspects: number;
    tags: number;
  };
}

const Step4RequirementTable = ({
  onNext,
  onBack,
  onCancel,
  isFirstStep,
  selectedCompanyUnit,
  connectionAddedToReqs,
  connectionRemovedFromReqs,
  requirementHasConnection,
  addRequirementConnection,
  removeRequirementConnection,
  currentStepName,
  updateConnectionInfo,
  setUpdateConnectionInfo,
}: Step4RequirementTableProps) => {
  const { api } = useAptorApi();
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const tableClasses = useConnectionTableStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [filters, setFilters] = useFilter();
  const tableRef = useRef<any>(null);
  const [isFiltersInitialized, setIsFiltersInitialized] = useState(false);
  const [visibleRows, setVisibleRows] = useState<IRequirement[]>([]);

  useEffect(() => {
    if (isFiltersInitialized) {
      tableRef.current && (tableRef.current as any).onQueryChange();
    }
  }, [filters, isFiltersInitialized]);

  useEffect(() => {
    if (selectedCompanyUnit) {
      setFilters('companyUnit', [selectedCompanyUnit.name]);
    }
    setIsFiltersInitialized(true);
  }, [selectedCompanyUnit, setFilters]);

  const handleDataFetch = useCallback(
    async (query: Query<any>) => {
      if (!isFiltersInitialized) {
        return { data: [], page: 0, totalCount: 0 };
      }
      setVisibleRows([]);
      const result = await api.searchCustomerRequirements<any>({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
        filters: filters,
      } as IGridQueryRequest);
      setVisibleRows(result.data || []);
      return result;
    },
    [api, filters, isFiltersInitialized]
  );

  const check = (req: any) => {
    let isSelected = requirementHasConnection(req.id);
    return {
      tooltip: formatMessage({
        id: isSelected ? 'component.mtable.unselectTooltip' : 'component.mtable.selectTooltip',
      }),
      icon: isSelected ? 'check_box' : 'check_box_outline_blank',
      isFreeAction: true,
      onClick: () => {
        if (isSelected) {
          removeRequirementConnection(req);
        } else {
          addRequirementConnection(req);
        }
      },
      iconProps: {
        style: { color: '#c4122f' },
      },
    };
  };

  const hasChanges = connectionAddedToReqs.length > 0 || connectionRemovedFromReqs.length > 0;
 const renderDate = (date?: Date) => {
    return date ? moment(date).format('ll') : undefined;
  };

  return (
    <div className={tableClasses.root} style={{ width: '100%' }}>
      <div style={{ width: '100%', padding: isMobile ? theme.spacing(2) : theme.spacing(3), backgroundColor: '#f7f0f1', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
        <Typography variant="body2" style={{ marginBottom: theme.spacing(2), fontSize: '16px' }}>
          {formatMessage({ id: 'law-portal.manage.requirement-list.update-requirement.description' })}
        </Typography>
        <RequirementsListFilter disable={true} companyUnit={selectedCompanyUnit} filters={filters} setFilter={setFilters} />
        <Box style={{ maxHeight: '500px', overflow: 'auto', marginTop: theme.spacing(2) }}>
          <MTable
            tableRef={tableRef}
            title={
              <Box style={{ display: 'flex', alignItems: 'center' }}>
                <Box style={{ width: 76, paddingLeft: 24, boxSizing: 'border-box', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                <Checkbox
                  checked={visibleRows.length > 0 && visibleRows.every(row => requirementHasConnection(row.id))}
                  indeterminate={visibleRows.some(row => requirementHasConnection(row.id)) && !visibleRows.every(row => requirementHasConnection(row.id))}
                  disabled={visibleRows.length === 0}
                  onChange={(_, checked) => {
                    visibleRows.forEach(row => {
                      if (requirementHasConnection(row.id) !== checked) check(row).onClick();
                    });
                  }}
                  style={{ color: '#c4122f' }}
                />
                </Box>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {formatMessage({ id: 'law-portal.evalution.table.selectAll' })}
                </Typography>
              </Box>
            }
            columns={[
              // { title: formatMessage({ id: 'law-portal.manage.law-list.table.number' }), field: 'number' },
              // {
              //   title: formatMessage({ id: 'law-portal.manage.law-list.table.law' }),
              //   field: 'name',
              //   render: (row) => <RepealableName name={row.name} effectiveTo={row.effectiveTo} />,
              // },
              // {
              //   title: formatMessage({ id: 'law-portal.manage.law-list.table.description' }),
              //   field: 'description',
              //   render: (row) => <HtmlView variant="body2" value={row.description} />,
              //   sorting: false,
              // },
              // { title: formatMessage({ id: 'law-portal.manage.law-list.table.area' }), field: 'area' },
              // {
              //   title: formatMessage({ id: 'law-portal.manage.law-list.table.notes' }),
              //   field: 'notes',
              //   render: (row) => <OwnNotes {...row} linkTo={`/law-portal/our-laws/${row.id}`} />,
              //   sorting: false,
              // },
              {
                title: formatMessage({ id: 'law-portal.manage.requirements.table.name' }),
                field: 'name',
                render: (row) => <RepealableName name={row?.name} effectiveTo={row.effectiveTo} />,
              },
              {
                title: formatMessage({ id: 'law-portal.manage.requirements.table.description' }),
                field: 'description',
                render: (data: IRequirement) => <HtmlView variant="body2" value={data.description} />,
                sorting: false,
              },
              { title: formatMessage({ id: 'law-portal.manage.requirements.table.area' }), field: 'area' },
              {
                title: formatMessage({ id: 'law-portal.manage.requirements.table.changedDate' }),
                field: 'changedDate',
                render: (row) => renderDate(row.changedDate),
              },
              {
                title: formatMessage({ id: 'law-portal.manage.requirements.table.notes' }),
                field: 'notes',
                render: (row) => <OwnNotes {...row} linkTo={`/law-portal/other-requirements/${row.id}`} />,
                sorting: false,
              },

            ]}
            actions={[check]}
            options={{ actionsColumnIndex: 0, showTitle: true, actionsCellStyle: { width: 100, minWidth: 100, maxWidth: 100, padding: 0 } }}
            actionLabel={formatMessage({ id: 'law-portal.manage.update-laws.connection' })}
            data={handleDataFetch}
            components={{ Actions: CenteredActions, Toolbar: ConnectionToolbar }}
          />
        </Box>
      </div>
      {/* <UpdateConnectionInfo
      updateConnectionInfo={updateConnectionInfo}
      currentStepName={currentStepName}
      onUpdate={setUpdateConnectionInfo}
    /> */}
      <Box>
        <StepButtonBar
          onCancel={onCancel}
          onBack={onBack}
          onNext={onNext}
          nextLabel="form.next"
          nextDisabled={!hasChanges}
          formatMessage={formatMessage}
          showBack={!isFirstStep}
        />
      </Box>
    </div>
  );
};

export default Step4RequirementTable;
